import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ILoja } from "shared/interfaces/ILoja";
import styles from "./VisualizarLoja.module.scss";
import { PencilLine } from "lucide-react";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import {
  buscarLojaPorId,
  uploadImagemLoja,
} from "shared/services/api/lojas.api";

export const VisualizarLoja: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loja, setLoja] = useState<ILoja | null>(null);
  const [loading, setLoading] = useState(true);

  /* ================================
     COR STATUS
  ================================ */

  const getCorStatus = (status?: ILoja["status"]) => {
    switch (status) {
      case "Ativo":
        return "#CFF6B5";
      case "Pendente":
        return "#FFE6A7";
      case "Bloqueado":
        return "#FFD2D2";
      case "Inativo":
        return "#c53d3dff";
      default:
        return "#E5E7EB";
    }
  };

  /* ================================
     BUSCAR LOJA
  ================================ */

  useEffect(() => {
    async function carregarLoja() {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const lojaEncontrada = await buscarLojaPorId(id);
        setLoja(lojaEncontrada);
      } catch (error) {
        console.error("Erro ao buscar loja:");
      } finally {
        setLoading(false);
      }
    }
    carregarLoja();
  }, [id]);

  /* ================================
     UPLOAD LOGO
  ================================ */

  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !loja?.id) return;
    if (file.size > 2 * 1024 * 1024) {
      alert("Imagem muito grande (máx 2MB)");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await uploadImagemLoja(id, file);
    } catch (error) {
      console.error("Erro ao enviar logo:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================================
     LOADING
  ================================ */

  if (loading) {
    return <p className={styles.loading}> Carregando loja... </p>;
  }
  if (!loja) {
    return <p className={styles.error}> Loja não encontrada </p>;
  }

  /* ================================
     RENDER
  ================================ */

  return (
    <PageContainer title="Minha Loja">
      <div className={styles.page}>
        <div className={styles.containerLoja}>
          <div className={styles.containerStatus}>
            {loja.status && (
              <span
                className={styles.status}
                style={{
                  backgroundColor: getCorStatus(loja.status),
                }}
              >
                {loja.status}
              </span>
            )}
          </div>

          <div className={styles.divImgLoja}>
            <div className={styles.logoContainer}>
              <img
                src={loja.logo || "/imagens/foto.png"}
                alt="Logo da loja"
                className={styles.logo}
              />
              <input
                type="file"
                accept="image/*"
                id="uploadLogo"
                onChange={handleUploadLogo}
                hidden
              />
              <label htmlFor="uploadLogo" className={styles.btnEdit}>
                <PencilLine size={12} />
              </label>
            </div>
            <div className={styles.divInfoLoja}>
              <h3 className={styles.title}>{loja.loja || "Loja"}</h3>
              <p className={styles.segmento}>{loja.segmento || ""}</p>
            </div>
          </div>
          <div className={styles.containerInfo}>
            <div className={styles.dadoSize}>
              <p className={styles.pLabel}>CNPJ:</p>
              {loja.cnpj || "Não informado"}
            </div>
            <div className={styles.dadoSize}>
              <p className={styles.pLabel}> Horário de Atendimento:</p>
              {loja.horario || "Não informado"}
            </div>

            <div className={styles.dadoSize}>
              <p className={styles.pLabel}>Telefone / WhatsApp:</p>
              {loja.telefone || "Não informado"}
            </div>

            <div className={styles.dadoSize}>
              <p className={styles.pLabel}>Email:</p>
              {loja.emailLoja || "Não informado"}
            </div>

            <div className={styles.dadoSize}>
              <p className={styles.pLabel}>CEP:</p>
              {loja.endereco?.cep || "Não informado"}
            </div>
            <div className={styles.dadoSize}>
              <p className={styles.pLabel}>Endereço:</p>
              {loja.endereco?.rua || "Não informado"}
              {loja.endereco?.numero ? `, ${loja.endereco.numero}` : ""}
            </div>
            <div className={styles.dadoSize}>
              <p className={styles.pLabel}>Bairro:</p>
              {loja.endereco?.bairro || "Não informado"}
            </div>
            <div className={styles.dadoSize}>
              <p className={styles.pLabel}> Cidade:</p>
              {loja.endereco?.cidade || "Não informado"}
              {loja.endereco?.estado ? ` / ${loja.endereco.estado}` : ""}
            </div>
            <div className={styles.dadoSize}>
              <p className={styles.pLabel}> Descrição: </p>
              {loja.descricao || "Sem descrição"}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};
